import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr, can } from "@/admin/api";
import { useAuth } from "@/admin/AuthContext";
import { PageTitle, StatCard, StatusBadge, fmtDate } from "@/admin/ui";
import {
  Users2, CalendarPlus, Tag, MessageSquare, TrendingUp, MousePointerClick,
  AlertCircle, ArrowRight, Plus, Images, Inbox, FileText, Loader2, CheckCircle2, ChevronRight,
} from "lucide-react";

const ACTIVITY_TEXT = {
  login: "signed in", create_offer: "created an offer", update_offer: "updated an offer",
  duplicate_offer: "duplicated an offer", archive_offer: "archived an offer",
  restore_offer: "restored an offer", delete_offer: "deleted an offer", reorder_offers: "reordered featured offers",
  update_inquiry: "updated an inquiry", delete_inquiry: "removed an inquiry", export_inquiries: "exported inquiries",
  create_user: "added a team member", update_user: "updated a team member", reset_password: "reset a password",
  delete_user: "removed a team member", create_gallery: "added a gallery photo", update_gallery: "updated a gallery photo",
  delete_gallery: "removed a gallery photo", update_homepage_content: "updated homepage content",
};

const SEVERITY = {
  high: "bg-red-50 border-red-100 text-red-700",
  medium: "bg-amber-50 border-amber-100 text-amber-700",
  low: "bg-sky-50 border-sky-100 text-sky-700",
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/dashboard").then(({ data }) => setData(data)).catch((e) => setErr(apiErr(e)));
  }, []);

  if (err) return <p className="text-red-600">{err}</p>;
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-terracotta" /></div>;

  const o = data.overview;
  const overviewCards = [
    { icon: Users2, label: "Travel List Signups", value: o.total_signups, testid: "stat-total-signups" },
    { icon: CalendarPlus, label: "New This Week", value: o.new_this_week, testid: "stat-new-week" },
    { icon: Tag, label: "Live Offers", value: o.active_offers, testid: "stat-active-offers" },
    { icon: MessageSquare, label: "Offer Inquiries", value: o.offer_inquiries, testid: "stat-offer-inquiries" },
    { icon: TrendingUp, label: "Conversion Rate", value: `${o.conversion_rate}%`, testid: "stat-conversion" },
    { icon: MousePointerClick, label: "Top CTA", value: o.most_clicked_cta, testid: "stat-cta" },
  ];

  const quickActions = [
    { key: "offers", label: "New Offer", icon: Plus, link: "/admin/offers" },
    { key: "gallery", label: "Add Gallery Photo", icon: Images, link: "/admin/gallery" },
    { key: "inquiries", label: "Review Inquiries", icon: Inbox, link: "/admin/inquiries" },
    { key: "content", label: "Edit Homepage", icon: FileText, link: "/admin/homepage" },
  ].filter((q) => can(user.role, q.key));

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const firstName = (user.name || "there").split(" ")[0];

  return (
    <div data-testid="admin-dashboard">
      <PageTitle title={`${greeting}, ${firstName}`} subtitle="Your KIS Living Vacations command center" />

      {/* Quick Actions */}
      {quickActions.length > 0 && (
        <div data-testid="quick-actions" className="flex flex-wrap gap-3 mb-8">
          {quickActions.map((q) => (
            <button
              key={q.key}
              data-testid={`quick-action-${q.key}`}
              onClick={() => navigate(q.link)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-ink text-white text-sm font-medium hover:bg-terracotta transition-colors"
            >
              <q.icon size={16} /> {q.label}
            </button>
          ))}
        </div>
      )}

      {/* Action Required */}
      <div data-testid="action-required" className="bg-white rounded-2xl border border-bone p-6 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle size={18} className="text-terracotta" />
          <h2 className="font-serif text-2xl font-semibold text-ink">Action Required</h2>
        </div>
        {data.action_required.length === 0 ? (
          <div className="flex items-center gap-2 text-ink-soft py-3">
            <CheckCircle2 size={18} className="text-green-600" /> You're all caught up — nothing needs attention right now.
          </div>
        ) : (
          <div className="space-y-2">
            {data.action_required.map((a) => (
              <button
                key={a.key}
                data-testid={`action-${a.key}`}
                onClick={() => navigate(a.link)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border text-left transition-transform hover:translate-x-0.5 ${SEVERITY[a.severity] || SEVERITY.low}`}
              >
                <span className="text-sm font-medium">{a.label}</span>
                <ArrowRight size={16} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Business Overview */}
      <h2 className="font-serif text-2xl font-semibold text-ink mb-4">Business Overview</h2>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {overviewCards.map((s) => <StatCard key={s.label} {...s} />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Submissions */}
        <div className="bg-white rounded-2xl border border-bone p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl font-semibold text-ink">Recent Signups</h2>
            {can(user.role, "inquiries") && (
              <button onClick={() => navigate("/admin/inquiries")} className="text-sm text-terracotta font-medium flex items-center gap-1 hover:gap-1.5 transition-all">
                View all <ChevronRight size={15} />
              </button>
            )}
          </div>
          <div className="divide-y divide-bone">
            {data.recent_submissions.length === 0 && <p className="text-ink-soft text-sm py-3">No submissions yet.</p>}
            {data.recent_submissions.map((s) => (
              <div key={s.id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-ink truncate">{s.first_name} · <span className="text-ink-soft font-normal">{s.email}</span></p>
                  <p className="text-xs text-ink-soft">{s.offer_title || s.travel_interest || "General"} · {fmtDate(s.created_at)}</p>
                </div>
                <StatusBadge status={s.status} />
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-2xl border border-bone p-6">
          <h2 className="font-serif text-2xl font-semibold text-ink mb-4">Recent Activity</h2>
          <div className="space-y-3">
            {data.recent_activity.length === 0 && <p className="text-ink-soft text-sm py-3">No activity yet.</p>}
            {data.recent_activity.map((a) => (
              <div key={a.id} className="flex items-start gap-3 text-sm">
                <span className="mt-1.5 h-2 w-2 rounded-full bg-terracotta shrink-0" />
                <div className="min-w-0">
                  <p className="text-ink">
                    <span className="font-medium">{(a.user_name || a.user_email || "Someone").split(" ")[0]}</span>{" "}
                    {ACTIVITY_TEXT[a.action] || a.action.replace(/_/g, " ")}
                  </p>
                  {a.detail && <p className="text-ink-soft text-xs truncate">{a.detail}</p>}
                  <p className="text-ink-soft/70 text-xs">{fmtDate(a.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
